import { useContext, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from 'components/context/AuthContext';
import { googleApi } from 'api';
import { toast } from 'react-hot-toast';
import { ModalsContext } from 'components/context/ModalsContext';

const GOOGLE_ACTIVE_STALE_MS = 3 * 60 * 1000;

export const googleActiveAccountQueryKey = (customerId) => ['googleAds', 'activeAccount', customerId ?? ''];

async function loadGoogleActiveAccountOrNull() {
  try {
    const res = await googleApi.fetchActiveGoogleAccount();
    return res.data;
  } catch (e) {
    const status = e.response?.status;
    const errorMessage = e.response?.data?.error || e.message || '';
    const benignNoActive =
      status === 404 ||
      /no\s+hay\s+cuenta\s+activa/i.test(errorMessage) ||
      /no\s+active\s+account/i.test(errorMessage);
    if (!benignNoActive) {
      console.error('❌ useGoogleAds: fetchActiveAccount error:', e);
      toast.error(`Error al obtener cuenta activa de Google Ads: ${errorMessage}`);
    }
    return null;
  }
}

/**
 * useGoogleAds hook: maneja conexión, desconexión y selección de cuenta para Google Ads.
 */
export const useGoogleAds = () => {
  const queryClient = useQueryClient();
  const { auth, updateContextUser, hasGoogleAdsConnected } = useContext(AuthContext);
  const {
    openConnectGoogleAdsAccountModal
  } = useContext(ModalsContext);

  const popupRef = useRef(null);

  // use effect and make it depend on auth.user.google_ads_connected for connectGoogleAds. instead of useCallback.
  const connectGoogleAds = useCallback((force = false) => {
    const _openSelectGoogleAccountModal = () => {
      updateContextUser({ ...auth.user, google_ads_connected: true });
      openConnectGoogleAdsAccountModal();
    };

    if (auth.user?.google_ads_connected && !force) {
      if (popupRef?.current && !popupRef?.current?.closed) {
        popupRef.current.close();
      }
      _openSelectGoogleAccountModal();
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Token de sesión no disponible');
      return;
    }
    const url = `${process.env.REACT_APP_API_URL}/google-ads/connect?token=${token}`;
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'GoogleAdsConnectPopup',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    );
    popupRef.current = popup;

    // Poll for popup close
    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
        return;
      }
    }, 3000);

    // Listen for messages from popup
    function handleMessage(event) {
      if (event.origin !== window.location.origin) return;
      const { data } = event;
      
      if (data.type === 'GOOGLE_AUTH_SUCCESS') {
        // Close the popup from parent
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        clearInterval(interval);
        _openSelectGoogleAccountModal();
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
      } else if (data.type === 'GOOGLE_AUTH_FAILURE') {
        // Close the popup from parent
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        clearInterval(interval);
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
        toast.error('Error al conectar Google Ads. Por favor, inténtalo de nuevo.');
      }
    }
    window.addEventListener('message', handleMessage);
  }, [auth?.user, updateContextUser, openConnectGoogleAdsAccountModal]);

  // A growth partner has no Google/Meta access (its brand's connection must not
  // leak into these calls — the backend 403s them for this user type).
  const isGrowthPartner = Boolean(auth?.user?.is_growth_partner);
  const isConnected = !!auth.user?.google_ads_connected && !isGrowthPartner;
  const hasGoogleCustomerId = !!auth.user?.google_ads_customer_id;
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAcc] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingCampaignById, setLoadingCampaignById] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [adminCampaigns, setAdminCampaigns] = useState([]);
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);

  const googleCustomerId = auth?.user?.google_ads_customer_id ?? null;
  const activeAccountQuery = useQuery({
    queryKey: googleActiveAccountQueryKey(googleCustomerId),
    queryFn: loadGoogleActiveAccountOrNull,
    enabled: hasGoogleAdsConnected && !isGrowthPartner,
    staleTime: GOOGLE_ACTIVE_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const activeAccount = hasGoogleAdsConnected ? activeAccountQuery.data ?? null : null;
  const loadingActiveAccount = activeAccountQuery.isLoading;


  /**
   * fetchAccounts
   * Recupera las cuentas de Google Ads asociadas al usuario desde el backend.
   */
  const fetchAccounts = useCallback(async (force = false) => {
    if (!isConnected) return;
    setLoadingAcc(true);
    try {
      const res = await googleApi.fetchGoogleAdsAccounts(force);
      setAccounts(res.data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar cuentas de Google Ads: ${errorMessage}`);
    } finally {
      setLoadingAcc(false);
    }
  }, [isConnected]);

  const fetchActiveAccount = useCallback(async () => {
    if (!hasGoogleAdsConnected) return undefined;
    return queryClient.fetchQuery({
      queryKey: googleActiveAccountQueryKey(googleCustomerId),
      queryFn: loadGoogleActiveAccountOrNull,
      staleTime: GOOGLE_ACTIVE_STALE_MS,
    });
  }, [hasGoogleAdsConnected, googleCustomerId, queryClient]);

  /**
   * setActiveGoogleAccount
   * Marca una cuenta de Ads como activa y actualiza los datos del usuario.
   */
  const setActiveGoogleAccount = useCallback(async (customerId) => {
    setLoading(true);
    try {
      const data = await googleApi.setActiveGoogleAdsAccount(customerId);
      updateContextUser(data);
      setAccounts(prev => prev.map(acc => ({
        ...acc,
        active: acc.customer_id === customerId
      })));
      // Fetch the updated active account to refresh AdsContext
      // Small delay to ensure backend has processed the change
      await new Promise(resolve => setTimeout(resolve, 500));
      await queryClient.invalidateQueries({ queryKey: ['googleAds', 'activeAccount'] });
      return data;
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      console.error('❌ Error in setActiveGoogleAccount:', e);
      toast.error(`Error al activar cuenta Google Ads: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [updateContextUser, queryClient]);

  /**
   * disconnect
   * Revoca el acceso a Google Ads y limpia el estado del usuario.
   */
  const disconnect = useCallback(async () => {
    setLoading(true);
    try {
      const data = await googleApi.disconnectGoogleAds();
      updateContextUser(data);
      setAccounts([]);
      setGoogleUserInfo(null);
      queryClient.removeQueries({ queryKey: ['googleAds', 'activeAccount'] });
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al desconectar cuenta Google Ads: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [updateContextUser, queryClient]);

  const fetchGoogleUserInfo = useCallback(async () => {
    if (!isConnected) return;

    setLoadingUserInfo(true);
    try {
      const res = await googleApi.fetchGoogleAdsUserInfo();
      setGoogleUserInfo(res.data);
      return res.data;
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al obtener info de usuario Google Ads: ${errorMessage}`);
      setGoogleUserInfo(null);
    } finally {
      setLoadingUserInfo(false);
    }
  }, [isConnected]);

  const fetchCampaigns = useCallback(
    async (params = {}) => {
      if (!hasGoogleAdsConnected) return;
  
      setLoadingCampaigns(true);
      try {
        const res = await googleApi.fetchGoogleAdsCampaigns(params);
        setCampaigns((prev) =>
          params.page > 1 ? [...prev, ...res.data] : res.data
        );
      } catch (e) {
        const errorMessage = e.response?.data?.error || e.message;
        toast.error(`Error al cargar campañas de Google Ads: ${errorMessage}`);
      } finally {
        setLoadingCampaigns(false);
      }
    },
    [hasGoogleAdsConnected]
  );

  // que es esto y por que se diferencia de fetchCampaigns?
  const fetchCampaignsForUser = useCallback(async (userId, params) => {
    if (!userId) return;
  
    setLoadingCampaigns(true);
    try {
      const res = await googleApi.fetchUserGoogleAdsCampaigns(userId, params); 
      setAdminCampaigns(res.data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar campañas del usuario de Google Ads: ${errorMessage}`);
      setAdminCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);
  
  const fetchCampaignById = useCallback(async (campaignId, { force = false, userId = null } = {}) => {
    if (!campaignId) return;
    setLoadingCampaignById(true);
    try {
      const res = userId
        ? await googleApi.fetchUserGoogleAdsCampaignById(userId, campaignId, { force })
        : await googleApi.fetchGoogleAdsCampaignById(campaignId, { force });
      return res.data;
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar campaña de Google Ads por ID: ${errorMessage}`);
    } finally {
      setLoadingCampaignById(false);
    }
  }, []);

  return {
    isConnected,
    hasGoogleCustomerId,
    loading,
    loadingAccounts,
    accounts,
    fetchAccounts,
    setActiveGoogleAccount,
    disconnect,
    fetchCampaigns,
    fetchCampaignsForUser,
    fetchCampaignById,
    fetchActiveAccount,
    activeAccount,
    loadingActiveAccount,
    campaigns,
    adminCampaigns,
    loadingCampaigns,
    loadingCampaignById,
    googleUserInfo,
    fetchGoogleUserInfo,
    loadingUserInfo,
    connectGoogleAds
  };  
};
